import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { WsApprovalRow } from "services/work-security-approvals.service";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import useIsPhone from "features/files/use-is-phone";
import { formatPrettyDate } from "utils/helperFunctions";
import { approvalKindKey } from "./approvals-helpers";

/**
 * Pending per-sphere approvals as a table — shared by the Tvarkos tab
 * «Laukia patvirtinimo» and the task list page «Tvarkos tvirtinimai». A row opens
 * the approval panel.
 */
type Props = {
  companyId: string;
  rows: WsApprovalRow[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  /** Hide the sphere column when it cannot distinguish anything. */
  showArea?: boolean;
};

export default function WsApprovalsTable({ companyId, rows, loading, error, onRetry, showArea = true }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isPhone = useIsPhone();

  if (loading && rows.length === 0) return <DataTableSkeleton rows={4} columns={showArea ? 5 : 4} />;

  if (error && rows.length === 0) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        <span>{t("ws_approvals_load_failed")}</span>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-sm border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
        >
          {t("retry")}
        </button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
        {t("ws_approvals_empty")}
      </div>
    );
  }

  const open = (row: WsApprovalRow) => navigate(`/app/${companyId}/work-security/approvals/${row.id}`);

  // Five columns do not fit a phone, so each approval is one tappable card there.
  if (isPhone) {
    return (
      <ul className="space-y-2">
        {rows.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => open(row)}
              className="block min-h-11 w-full rounded-md border border-gray-200 bg-white p-3 text-left hover:bg-gray-50"
            >
              <span className="flex items-start justify-between gap-3">
                <span className="font-medium text-blue-700">{row.user?.name || "—"}</span>
                <span className="shrink-0 text-xs text-gray-500">{formatPrettyDate(row.createdAt)}</span>
              </span>
              <span className="mt-1 block text-sm text-gray-700">{(row.positions || []).join(", ") || "—"}</span>
              <span className="mt-1 block text-xs text-gray-500">
                {[t(approvalKindKey(row.kind)), showArea ? row.area?.name : ""].filter(Boolean).join(" · ")}
              </span>
            </button>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
            <th className="py-2 pr-4">{t("employee")}</th>
            <th className="py-2 pr-4">{t("positions_title")}</th>
            <th className="py-2 pr-4">{t("type")}</th>
            {showArea && <th className="py-2 pr-4">{t("ws_approvals_col_area")}</th>}
            <th className="py-2 pr-4">{t("created_at")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="cursor-pointer border-b border-gray-100 align-top hover:bg-gray-50"
              onClick={() => open(row)}
            >
              <td className="py-2 pr-4">
                <button
                  type="button"
                  className="text-left font-medium text-blue-700 hover:underline"
                  onClick={(event) => {
                    event.stopPropagation();
                    open(row);
                  }}
                >
                  {row.user?.name || "—"}
                </button>
              </td>
              <td className="py-2 pr-4">{(row.positions || []).join(", ") || "—"}</td>
              <td className="py-2 pr-4">{t(approvalKindKey(row.kind))}</td>
              {showArea && <td className="py-2 pr-4">{row.area?.name || "—"}</td>}
              <td className="py-2 pr-4 whitespace-nowrap">{formatPrettyDate(row.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
